import React, { useState } from 'react';
import { 
  Ticket, 
  ShoppingCart, 
  TrendingUp, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  Link2,
  Shuffle,
  ArrowUpDown,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Minimize2,
  Maximize2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../i18n/LanguageContext';

const KPICard = ({ icon: Icon, label, value, subValue, valueAmount, colorClass, testId, onClick, billets, transactions, ticketsLabel, transactionsLabel }) => (
  <div 
    data-testid={testId}
    onClick={onClick}
    className={cn(
      "kpi-card relative overflow-hidden group",
      onClick ? "cursor-pointer hover:scale-105 transition-transform" : "cursor-default"
    )}
  >
    <div className="flex items-start justify-between relative z-10">
      <div>
        <p className="text-xs uppercase tracking-wide text-white font-semibold mb-1">
          {label}
        </p>
        {billets ? (
          <>
            <p className="text-2xl font-heading font-bold text-white">
              {billets} <span className="text-sm font-normal text-zinc-400">{ticketsLabel || 'billets'}</span>
            </p>
            {transactions && transactions !== billets && (
              <p className="text-xs text-zinc-400 mt-0.5">
                {transactions} {transactionsLabel || 'transactions'}
              </p>
            )}
          </>
        ) : (
          <p className="text-2xl font-heading font-bold text-white">
            {value}
          </p>
        )}
        {subValue && (
          <p className="text-xs text-[#C4A052]/80 mt-1 font-mono">
            {subValue}
          </p>
        )}
        {valueAmount > 0 && (
          <p className="text-xs text-emerald-400 mt-1 font-mono">
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(valueAmount)}
          </p>
        )}
      </div>
      <div className={cn('p-2 rounded-lg', colorClass)}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#C4A052]/50 to-transparent" />
  </div>
);

export const StatsCards = ({ stats, project, onTabChange, onFilter }) => {
  const { t } = useLanguage();
  const isRG = project === 'ROLAND GARROS';
  const [expanded, setExpanded] = useState(() => localStorage.getItem('yas_stats_expanded') === '1');
  const [minimized, setMinimized] = useState(() => localStorage.getItem('yas_stats_minimized') === '1');

  const toggleExpanded = () => {
    setExpanded((prev) => {
      localStorage.setItem('yas_stats_expanded', prev ? '0' : '1');
      return !prev;
    });
  };

  const toggleMinimized = () => {
    setMinimized((prev) => {
      localStorage.setItem('yas_stats_minimized', prev ? '0' : '1');
      return !prev;
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // 📏 Mode ultra-réduit : une seule ligne compacte
  if (minimized) {
    return (
      <div data-testid="stats-mini-bar" className="kpi-card flex items-center gap-3 md:gap-4 px-3 !py-1.5 text-xs md:text-sm overflow-x-auto whitespace-nowrap">
        <button
          data-testid="mini-kpi-ventes"
          onClick={() => { onTabChange?.('vente'); onFilter?.(null); }}
          className="flex items-center gap-1.5 hover:opacity-75 transition-opacity"
        >
          <TrendingUp className="h-3.5 w-3.5 text-red-400" />
          <span className="text-zinc-400 uppercase font-semibold text-[10px] md:text-xs">{t('sales')}</span>
          <span className="text-white font-bold">{stats?.vente_qty || stats?.total_ventes || 0}</span>
          <span className="text-emerald-400 font-mono hidden lg:inline text-[10px]">{formatCurrency(stats?.total_vente_value)}</span>
        </button>
        <span className="text-zinc-700">|</span>
        <button
          data-testid="mini-kpi-achats"
          onClick={() => { onTabChange?.('achat'); onFilter?.(null); }}
          className="flex items-center gap-1.5 hover:opacity-75 transition-opacity"
        >
          <ShoppingCart className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-zinc-400 uppercase font-semibold text-[10px] md:text-xs">{t('purchases')}</span>
          <span className="text-white font-bold">{stats?.achat_qty || stats?.total_achats || 0}</span>
        </button>
        <span className="text-zinc-700">|</span>
        <button
          data-testid="mini-kpi-delta"
          onClick={() => { onTabChange?.('delta'); onFilter?.(null); }}
          className="flex items-center gap-1.5 hover:opacity-75 transition-opacity"
        >
          <ArrowUpDown className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-white font-bold tracking-wider">DELTA</span>
        </button>
        <button
          data-testid="stats-maximize-btn"
          onClick={toggleMinimized}
          className="ml-auto flex items-center gap-1 text-[10px] md:text-xs text-[#C4A052] hover:text-[#D4B86A] transition-colors uppercase tracking-wider font-semibold"
          title="Agrandir les cartes"
        >
          <Maximize2 className="h-3.5 w-3.5" /> Agrandir
        </button>
      </div>
    );
  }

  return (
    <div className={cn(
      "grid grid-cols-2 gap-2 md:gap-3",
      expanded
        ? cn("sm:grid-cols-3", isRG ? "lg:grid-cols-9" : "lg:grid-cols-10")
        : "sm:grid-cols-4"
    )}>
      <KPICard
        testId="kpi-ventes"
        icon={TrendingUp}
        label={t('sales')}
        billets={stats?.vente_qty || stats?.total_ventes || 0}
        transactions={stats?.total_ventes || 0}
        ticketsLabel={t('tickets')}
        transactionsLabel={t('transactions')}
        subValue={formatCurrency(stats?.total_vente_value)}
        colorClass="bg-red-400/15 text-red-400"
        onClick={() => { onTabChange?.('vente'); onFilter?.(null); }}
      />
      <KPICard
        testId="kpi-achats"
        icon={ShoppingCart}
        label={t('purchases')}
        billets={stats?.achat_qty || stats?.total_achats || 0}
        transactions={stats?.total_achats || 0}
        ticketsLabel={t('tickets')}
        transactionsLabel={t('transactions')}
        subValue={formatCurrency(stats?.total_achat_value)}
        colorClass="bg-emerald-400/15 text-emerald-400"
        onClick={() => { onTabChange?.('achat'); onFilter?.(null); }}
      />
      {/* DELTA */}
      <div 
        data-testid="kpi-delta"
        onClick={() => { onTabChange?.('delta'); onFilter?.(null); }}
        className="kpi-card relative overflow-hidden group cursor-pointer hover:scale-105 transition-transform flex items-center justify-center"
      >
        <div className="flex flex-col items-center gap-1">
          <div className="p-2 rounded-lg bg-emerald-400/15 text-emerald-400">
            <ArrowUpDown className="h-5 w-5" />
          </div>
          <p className="text-lg font-heading font-bold text-white tracking-wider">DELTA</p>
        </div>
      </div>

      {expanded && (
        <>
          {/* RG: VALIDÉS (vert) — WC: LIVRÉS/VALIDÉS (vert) */}
          {isRG ? (
            <KPICard
              testId="kpi-validated"
              icon={CheckCircle2}
              label="VALIDÉS"
              value={stats?.validated || 0}
              subValue={`V: ${stats?.validated_vente || 0} | A: ${stats?.validated_achat || 0}`}
              valueAmount={stats?.validated_value || 0}
              colorClass="bg-emerald-400/15 text-emerald-400"
              onClick={() => { onTabChange?.('valides'); onFilter?.(null); }}
            />
          ) : (
            <KPICard
              testId="kpi-validated"
              icon={CheckCircle2}
              label={t('delivered')}
              value={stats?.validated || 0}
              subValue={`V: ${stats?.validated_vente || 0} | A: ${stats?.validated_achat || 0}`}
              valueAmount={stats?.validated_value || 0}
              colorClass="bg-emerald-400/15 text-emerald-400"
              onClick={() => { onTabChange?.('tout'); onFilter?.('VALIDE'); }}
            />
          )}

          {/* Carte fusionnée ATTRIBUÉS (V + A) — comme l'onglet ATTRIBUÉS du tableau */}
          <KPICard
            testId="kpi-attribues"
            icon={Link2}
            label="ATTRIBUÉS"
            value={(stats?.ventes_attribuees || 0) + (stats?.achats_attribues || 0)}
            subValue={`V: ${stats?.ventes_attribuees || 0} (${stats?.ventes_attribuees_qty || stats?.attribue_qty || 0}) · A: ${stats?.achats_attribues || 0} (${stats?.achats_attribues_qty || 0})`}
            colorClass="bg-blue-400/15 text-blue-400"
            onClick={() => { onTabChange?.(isRG ? 'attribues' : 'tout'); onFilter?.(isRG ? null : 'attribue'); }}
          />
          {/* Carte ATTR. PARTIELLE (bleu ciel) */}
          <KPICard
            testId="kpi-partial"
            icon={AlertCircle}
            label="ATTR. PARTIELLE"
            value={`${stats?.partial_ventes_count || 0}${(stats?.partial_ventes_total_qty || 0) > 0 ? ` (${stats?.partial_ventes_linked_qty || 0}/${stats?.partial_ventes_total_qty || 0})` : ''}`}
            subValue={(stats?.partial_ventes_count || 0) > 0
              ? `Restants: ${stats?.partial_ventes_missing || 0} · ACHAT liés: ${stats?.partial_achats_count || 0}`
              : 'Pré-attribuer < qty depuis Attribuer'}
            colorClass="bg-sky-400/15 text-sky-400"
            onClick={() => { onTabChange?.('partials'); onFilter?.(null); }}
          />
          <KPICard
            testId="kpi-special"
            icon={Shuffle}
            label={t('specialAttr')}
            value={stats?.special || 0}
            subValue={`V: ${stats?.special_vente || 0} | A: ${stats?.special_achat || 0}`}
            colorClass="bg-amber-400/15 text-amber-400"
            onClick={() => { onTabChange?.('tout'); onFilter?.('SPECIAL_MATCH'); }}
          />

          {/* RG: LIVRÉS (rouge) — WC: EN ATTENTE */}
          {isRG ? (
            <KPICard
              testId="kpi-livre"
              icon={Ticket}
              label="LIVRÉS"
              value={stats?.livre || 0}
              subValue={`V: ${stats?.livre_vente || 0} | A: ${stats?.livre_achat || 0}`}
              valueAmount={stats?.livre_value || 0}
              colorClass="bg-red-400/15 text-red-400"
              onClick={() => { onTabChange?.('livres'); onFilter?.(null); }}
            />
          ) : (
            <KPICard
              testId="kpi-pending"
              icon={Clock}
              label={t('pending')}
              value={stats?.pending || 0}
              colorClass="bg-zinc-400/15 text-zinc-400"
              onClick={() => { onTabChange?.('tout'); onFilter?.('PENDING'); }}
            />
          )}

          {/* RG: pas de carte ANNULÉS — WC: ANNULÉS */}
          {!isRG && (
            <KPICard
              testId="kpi-cancelled"
              icon={XCircle}
              label={t('cancelled')}
              value={stats?.cancelled || 0}
              subValue={`V: ${stats?.cancelled_vente || 0} | A: ${stats?.cancelled_achat || 0}`}
              colorClass="bg-red-400/15 text-red-400"
              onClick={() => { onTabChange?.('tout'); onFilter?.('ANNULEE'); }}
          />
          )}
        </>
      )}

      {/* Carte toggle VOIR PLUS / VOIR MOINS + Réduire */}
      <div className="kpi-card relative overflow-hidden group flex flex-col items-center justify-center gap-1.5 border-dashed">
        <button
          data-testid="stats-toggle-more"
          onClick={toggleExpanded}
          className="flex flex-col items-center gap-1 hover:scale-105 transition-transform"
        >
          <div className="p-1.5 rounded-lg bg-[#C4A052]/15 text-[#C4A052]">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
          <p className="text-xs font-heading font-bold text-[#C4A052] tracking-wider uppercase">
            {expanded ? 'Voir moins' : 'Voir plus'}
          </p>
        </button>
        <button
          data-testid="stats-minimize-btn"
          onClick={toggleMinimized}
          className="flex items-center gap-1 text-[10px] text-zinc-400 hover:text-[#C4A052] transition-colors uppercase tracking-wider"
          title="Réduire en barre compacte"
        >
          <Minimize2 className="h-3 w-3" /> Réduire
        </button>
      </div>
    </div>
  );
};

export default StatsCards;
